import React from 'react'
import { ModalProps, useModal } from '../modal'
import Modal from '../modal'
import WorkPeriodTagForm, { WorkPeriodTagFormProps } from './form'
import { Box, Button, Element } from 'react-bulma-components'
import ConfirmationModal from '../confirmation-modal/confirmation-modal'
import { useUpdateMutation } from '../../queries/work-period-tags'

interface WorkPeriodTagModalFormProps extends WorkPeriodTagFormProps {
  isDisplayed: ModalProps['actions']['isDisplayed']
  setIsDisplayed: ModalProps['actions']['setIsDisplayed']
}

/**
 * Modal form component for creating or editing work period tags
 * Includes form fields and a button to activate/deactivate the tag
 */
const WorkPeriodTagModalForm: React.FC<WorkPeriodTagModalFormProps> = ({
  isDisplayed,
  setIsDisplayed,
  tag,
  onSuccess,
}) => {
  const updateMutation = useUpdateMutation()
  const { isDisplayed: isConfirmationDisplayed, setIsDisplayed: setIsConfirmationDisplayed } =
    useModal()

  const isTagInactive = tag?.isInactive

  const handleToggleStatus = async () => {
    if (!tag?._id) return
    await updateMutation.mutateAsync({ id: tag._id, isInactive: !isTagInactive })
    setIsDisplayed(false)
    setIsConfirmationDisplayed(false)
  }

  const handleFormSuccess = () => {
    setIsDisplayed(false)
    if (onSuccess) {
      onSuccess()
    }
  }

  return (
    <>
      <Modal
        actions={{ isDisplayed, setIsDisplayed }}
        title={tag ? 'Modifier la catégorie' : 'Créer une nouvelle catégorie'}
        dataTest="work-period-tag-modal"
      >
        <Box>
          <WorkPeriodTagForm tag={tag} onSuccess={handleFormSuccess} />
        </Box>
        {tag && (
          <Box pt={2}>
            <Element display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Element>
                <Element textWeight="bold">
                  {isTagInactive ? 'Réactiver cette catégorie' : 'Désactiver cette catégorie'}
                </Element>
                <Element textSize={7} textColor="grey">
                  {isTagInactive
                    ? 'Une fois réactivée, la catégorie sera à nouveau disponible pour les journées de travail'
                    : 'Une fois désactivée, vous pourrez la réactiver plus tard'}
                </Element>
              </Element>
              <Button
                color={isTagInactive ? 'success' : 'danger'}
                onClick={() => setIsConfirmationDisplayed(true)}
                data-test="disable-work-period-tag-button"
                size="small"
              >
                {isTagInactive ? 'Réactiver la catégorie' : 'Désactiver la catégorie'}
              </Button>
            </Element>
          </Box>
        )}
      </Modal>

      <ConfirmationModal
        show={isConfirmationDisplayed}
        onConfirm={handleToggleStatus}
        onCancel={() => setIsConfirmationDisplayed(false)}
        title={isTagInactive ? 'Réactiver la catégorie' : 'Désactiver la catégorie'}
        confirmButtonLabel={isTagInactive ? 'Réactiver' : 'Désactiver'}
        confirmButtonProps={{
          color: isTagInactive ? 'success' : 'danger',
          disabled: false,
        }}
      >
        <>
          {isTagInactive ? (
            <>
              Êtes-vous sûr de vouloir réactiver cette catégorie ?
              <br />
              La catégorie sera à nouveau disponible pour les journées de travail.
            </>
          ) : (
            <>
              Êtes-vous sûr de vouloir désactiver cette catégorie ?
              <br />
              Les journées de travail utilisant cette catégorie ne seront pas impactées et
              conserveront leur catégorie.
              <br />
              <br />
              <Element textWeight="bold">
                La catégorie ne sera plus visible dans les filtres et ne pourra plus être
                sélectionnée pour les nouvelles journées de travail.
              </Element>
            </>
          )}
        </>
      </ConfirmationModal>
    </>
  )
}

export default WorkPeriodTagModalForm
